import { useEffect } from "react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import SEO from "@/components/shared/SEO";
import { ShieldCheck, Users, HeartHandshake, Stethoscope, Mail, Phone } from "lucide-react";

export function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title="About BestRx · Provider-Guided Wellness"
        path="/about"
        description="BestRx connects patients with licensed clinicians and partnered compounding pharmacies for transparent, bloodwork-driven wellness programs."
      />
      <Nav />
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute -top-20 -left-40 w-[480px] h-[480px] rounded-full blur-3xl bg-[var(--leen-pink)]/20" />
        <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full blur-3xl bg-[var(--leen-blue)]/20" />
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1200px] mx-auto">
          <p className="font-script italic text-[var(--leen-pink)]" style={{ fontSize: "clamp(48px,6vw,96px)", lineHeight: 1 }}>About</p>
          <h1 className="font-display text-[52px] sm:text-[88px] md:text-[120px] lg:text-[150px] leading-[0.88] tracking-tight -mt-2">
            BEST<span className="text-[var(--leen-blue)]">RX</span>.ONLINE
          </h1>
          <p className="mt-7 font-body italic text-[18px] md:text-[22px] text-[var(--leen-ink-soft)] max-w-3xl">
            Provider-guided wellness, delivered with intention. We exist to make medically-supervised
            programs in weight loss, hormones, performance, and longevity accessible without the
            waiting rooms.
          </p>
        </div>
      </section>

      <section className="bg-[var(--leen-card)] py-16 md:py-24 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p className="eyebrow text-[var(--leen-pink)] mb-4">Our Mission</p>
            <h2 className="font-display text-[32px] md:text-[44px] leading-tight mb-5">REAL MEDICINE.<br />REAL OUTCOMES.</h2>
            <p className="text-[15px] text-[var(--leen-ink-soft)] leading-relaxed italic">
              BestRx connects patients with licensed providers and partnered compounding pharmacies for
              clinically-supervised programs. We focus on transparent pricing, bloodwork-driven
              protocols, and ongoing care — not one-shot prescriptions.
            </p>
          </div>
          <div>
            <p className="eyebrow text-[var(--leen-blue)] mb-4">How It Works</p>
            <h2 className="font-display text-[32px] md:text-[44px] leading-tight mb-5">PROVIDER-GUIDED.<br />PATIENT-LED.</h2>
            <p className="text-[15px] text-[var(--leen-ink-soft)] leading-relaxed italic">
              Every program at BestRx is reviewed by a licensed provider. Treatment is determined by
              provider review based on your qualification details and medical intake. Individual
              results vary — and that's exactly why we work case by case.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <p className="font-script italic text-[var(--leen-pink)] -mb-2" style={{ fontSize: "clamp(42px,5vw,60px)", lineHeight: 1 }}>The</p>
          <h2 className="font-display text-[36px] md:text-[56px] leading-none mb-10">BESTRX PHILOSOPHY</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: ShieldCheck, t: "Provider Guided", b: "Licensed clinicians review every program before any medication ships." },
              { icon: Stethoscope, t: "Bloodwork Driven", b: "We don't prescribe blind. Labs inform every protocol where clinically indicated." },
              { icon: HeartHandshake, t: "Ongoing Care", b: "Care doesn't end after delivery. Message your care team anytime." },
              { icon: Users, t: "HIPAA-Conscious", b: "Your health information is protected end-to-end." },
            ].map(({ icon: Icon, t, b }) => (
              <div key={t} className="bg-[var(--leen-card)] border border-[var(--leen-line)] rounded-xl p-6">
                <span className="w-10 h-10 rounded-md bg-[var(--leen-pink)]/15 border border-[var(--leen-pink)]/40 flex items-center justify-center text-[var(--leen-pink)] mb-5"><Icon size={18} /></span>
                <h3 className="font-display text-[18px] mb-2">{t}</h3>
                <p className="text-[13px] text-[var(--leen-ink-soft)] leading-relaxed italic">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
}

export function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title="Contact BestRx · Care Team"
        path="/contact"
        description="Reach the BestRx care team for questions about programs, eligibility, or your existing care plan. We respond within one business day."
      />
      <Nav />
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute -top-20 -right-40 w-[480px] h-[480px] rounded-full blur-3xl bg-[var(--leen-blue)]/20" />
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1100px] mx-auto">
          <p className="font-script italic text-[var(--leen-pink)]" style={{ fontSize: "clamp(48px,6vw,96px)", lineHeight: 1 }}>Reach</p>
          <h1 className="font-display text-[52px] sm:text-[88px] md:text-[120px] lg:text-[150px] leading-[0.88] tracking-tight -mt-2">
            THE <span className="text-[var(--leen-pink)]">CARE TEAM</span>
          </h1>
          <p className="mt-7 font-body italic text-[18px] text-[var(--leen-ink-soft)] max-w-2xl">
            Questions about programs, qualification, or your existing care plan? Reach out — we
            respond within one business day.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="tel:+12148155966" className="bg-[var(--leen-card)] border border-[var(--leen-line)] hover:border-[var(--leen-pink)] rounded-xl p-6 block" data-testid="contact-phone">
              <Phone size={20} className="text-[var(--leen-pink)] mb-4" />
              <p className="eyebrow text-[var(--leen-ink-soft)] mb-2">Phone</p>
              <p className="font-display text-[22px]">(214) 815-5966</p>
              <p className="text-[12px] text-[var(--leen-ink-mute)] mt-1 italic">Mon–Fri · 8a–6p CT</p>
            </a>
            <a href="mailto:care@bestrx.online" className="bg-[var(--leen-card)] border border-[var(--leen-line)] hover:border-[var(--leen-blue)] rounded-xl p-6 block" data-testid="contact-email">
              <Mail size={20} className="text-[var(--leen-blue)] mb-4" />
              <p className="eyebrow text-[var(--leen-ink-soft)] mb-2">Email</p>
              <p className="font-display text-[20px]">care@bestrx.online</p>
              <p className="text-[12px] text-[var(--leen-ink-mute)] mt-1 italic">Responses within 24h</p>
            </a>
            <a href="#start-assessment" className="bg-gradient-pb rounded-xl p-6 block" data-testid="contact-qualify">
              <p className="eyebrow text-white/90 mb-4">Fast Path</p>
              <p className="font-display text-[22px] text-white">Check eligibility</p>
              <p className="text-[12px] text-white/80 mt-1 italic">4 minutes · No commitment</p>
            </a>
          </div>
        </div>
      </section>
      <FinalCTA />
      <Footer />
    </main>
  );
}
